import { take } from 'rxjs/operators';
import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import { getSelectedLocationIds } from '@app/data/helpers/locationHelpers';
import { defaultColour, getMauriColour } from '@app/widgets/helpers/rongo-colours';
import { RongoReportingData } from '@app/widgets/helpers/types';

interface MauriData {
  mauri: string;
}

@Component({
  selector: 'app-rongo-mauri',
  templateUrl: './rongo-mauri.component.html',
  styleUrls: ['./rongo-mauri.component.scss'],
})
export class MauriComponent implements OnInit {
  @ViewChild('textEl') textEl!: ElementRef;
  @ViewChild('wrapperEl') wrapperEl!: ElementRef;

  chartTitle: string = 'Rongo Mauri';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-mauri';
  startDate: any = '';
  endDate: any = '';
  data: MauriData = { mauri: 'N/A' };
  mauriWord: string = '';
  mauriWordColour: string = defaultColour;
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  locationIds: string[] = [];

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.locationIds = getSelectedLocationIds(this.filterService.getFilters().selectedLocations);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getMauriData(this.startDate, this.endDate, this.locationIds).subscribe(
      (data: RongoReportingData<MauriData>) => {
        const { config, data: mauriData } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.data = mauriData;
        this.setChartData();
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };

  setChartData = () => {
    this.mauriWord = this.data.mauri;
    this.mauriWordColour = getMauriColour(this.data.mauri);
    this.noData = this.mauriWord === 'N/A';

    // Trigger font-size recalculation
    setTimeout(() => this.fitTextToHeight());
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const newLocationIds = getSelectedLocationIds(filters.filters.selectedLocations);
    const locationsChanged = newLocationIds.join(',') !== this.locationIds.join(',');
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || locationsChanged || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.locationIds = newLocationIds;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  private fitTextToHeight() {
    if (!this.textEl || !this.wrapperEl || !this.textEl.nativeElement || !this.wrapperEl.nativeElement) {
      return;
    }
    const wrapperHeight = this.wrapperEl.nativeElement.clientHeight;
    const textElement = this.textEl.nativeElement as HTMLElement;

    let fontSize = 100;
    textElement.style.fontSize = fontSize + 'px';

    while (textElement.scrollHeight > wrapperHeight - 100 && fontSize > 10) {
      fontSize -= 1;
      textElement.style.fontSize = fontSize + 'px';
    }
  }
}
